import { useState } from "react";
import { useNavigate } from "react-router-dom";
import RoomGallery from "./RoomGallery";
import { useContext } from "react";
import { AuthContext } from "../../clients/utils/AuthContext";

function RoomList({ rooms, checkIn, checkOut, prices }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [openRates, setOpenRates] = useState({});
  const [selectedRates, setSelectedRates] = useState({});

  const nights =
    checkIn && checkOut
      ? Math.ceil(
          (new Date(checkOut) - new Date(checkIn)) /
            (1000 * 60 * 60 * 24)
        )
      : 0;

  const getPolicyInfo = (rate) => {
    const policy = rate.cancellationPolicy;
    const now = new Date();
    const checkInDate = new Date(checkIn);
    const diffHours = (checkInDate.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (!policy) return {
      policyText: "Conditions à confirmer avec l'hôtel",
      policyColor: "text-gray-500"
    };

    if (policy.type === "free") {
      const isFree = diffHours >= policy.hoursBeforeArrival;
      return {
        policyText: `Annulation gratuite jusqu'à ${policy.hoursBeforeArrival}h avant`,
        policyColor: isFree ? "text-green-600" : "text-red-500"
      };
    }

    if (policy.type === "partial") {
      return {
        policyText: `Gratuit jusqu'à ${policy.hoursBeforeArrival}h avant, puis ${policy.penaltyPercentage}% de frais`,
        policyColor: "text-yellow-600"
      };
    }

    if (policy.type === "non_refundable") {
      return {
        policyText: "Non remboursable",
        policyColor: "text-red-500"
      };
    }

    return { policyText: "", policyColor: "text-gray-500" };
  };

  return (
    <div className="mt-12" id="rooms">

      {/* HEADER */}
      <div className="mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">
          Chambres disponibles
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Choisissez la chambre qui correspond à votre séjour
        </p>
      </div>

      {/* LIST */}
      <div className="flex flex-col gap-6">
        {rooms?.map((room) => {

          const roomData = prices?.find(
            p => p.roomId.toString() === room._id.toString()
          );
          const isDisabled = (checkIn && checkOut) && !roomData;
          const dispo = roomData?.available ?? 0;

          const rates = roomData?.prices || [];
          const cheapestRate = roomData?.bestPrice || null;
          const selectedRate = selectedRates[room._id] || cheapestRate;
          const total = selectedRate?.total || null;
          const isOpen = openRates[room._id] || true;

          return (
            <div
              key={room._id}
              className="group bg-white rounded-2xl border border-gray-100 p-4 flex flex-col gap-6 hover:shadow-xl transition-shadow duration-300"
            >

              {/* TOP — image + infos + prix */}
              <div className="flex flex-col md:flex-row gap-6">

                {/* IMAGE */}
                <RoomGallery photos={room.photos} />

                {/* INFOS */}
                <div className="flex-1">

                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {room.title}
                    </h3>

                    {/* BADGE DISPO */}
                    {checkIn && checkOut && (
                      dispo > 0 ? (
                        dispo <= 2 ? (
                          <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded-md font-medium">
                            Plus que {dispo} restantes
                          </span>
                        ) : (
                          <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded-md font-medium">
                            Disponible
                          </span>
                        )
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-md font-medium">
                          Complet
                        </span>
                      )
                    )}
                  </div>

                  <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                    {room.desc}
                  </p>

                  <p className="text-xs text-gray-500 mt-2">
                    Jusqu'à {room.maxPeople} personnes
                  </p>

                  {(!checkIn || !checkOut) && (
                    <p className="text-xs text-orange-500 mt-2">
                      Sélectionnez vos dates pour réserver
                    </p>
                  )}

                  {/* FEATURES */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {room.features?.map((f, i) => (
                      <span
                        key={i}
                        className="text-xs bg-gray-100 px-2 py-1 rounded-md text-gray-600"
                      >
                        {f}
                      </span>
                    ))}
                  </div>

                </div>

                {/* PRIX + CTA */}
                <div className="flex flex-col justify-between items-end w-full md:w-auto md:min-w-[180px]">

                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      {checkIn && checkOut ? "Séjour total" : "À partir de"}
                    </p>

                    <p className="text-2xl font-semibold text-gray-900">
                      {checkIn && checkOut
                        ? total ? `${total} €` : "—"
                        : cheapestRate?.pricePerNight
                          ? `${cheapestRate.pricePerNight} €`
                          : "—"
                      }
                    </p>

                    {checkIn && checkOut && (
                      <p className="text-xs text-gray-400 mt-1">
                        {nights} nuit{nights > 1 && "s"}
                      </p>
                    )}

                    {/* Tarif sélectionné */}
                    {selectedRate && (
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedRate.ratePlanName}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 w-full mt-4">

                    {/* Bouton voir tarifs */}
                    {checkIn && checkOut && rates.length > 0 && (
                      <button
                        onClick={() =>
                          setOpenRates(prev => ({
                            ...prev,
                            [room._id]: !prev[room._id]
                          }))
                        }
                        className="w-full px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:border-gray-300 transition"
                      >
                        {isOpen ? "Masquer les tarifs" : "Voir les tarifs"}
                      </button>
                    )}

                    {/* Bouton réserver */}
                    <button
                      onClick={() => {
                        if (!checkIn || !checkOut) {
                          document.getElementById("booking")?.scrollIntoView({
                            behavior: "smooth"
                          });
                          return;
                        }
                        navigate(
                          `/checkout?room=${room._id}&checkIn=${checkIn}&checkOut=${checkOut}&ratePlanId=${selectedRate?.ratePlanId}`
                        );
                      }}
                      disabled={isDisabled}
                      className={`
                        w-full px-5 py-2.5 rounded-lg text-sm font-medium
                        transition-all duration-200
                        ${isDisabled
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-[#3A9D23] text-white hover:bg-[#33891E] active:scale-[0.97]"
                        }
                      `}
                    >
                      {!checkIn || !checkOut
                        ? "Choisir des dates"
                        : isDisabled
                        ? "Indisponible"
                        : "Réserver"}
                    </button>

                  </div>

                </div>

              </div>

              {/* TARIFS EXPANDABLES */}
              {isOpen && rates.length > 0 && (
                <div className="border-t pt-4 flex flex-col gap-3">

                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Choisissez votre tarif
                  </p>

                  {rates.map((rate, i) => {
                    const isSelected =
                      selectedRate?.ratePlanId?.toString() === rate.ratePlanId?.toString();
                    const { policyText, policyColor } = getPolicyInfo(rate);

                    return (
                      <div
                        key={i}
                        onClick={() =>
                          setSelectedRates(prev => ({
                            ...prev,
                            [room._id]: rate
                          }))
                        }
                        className={`
                          p-4 rounded-xl border cursor-pointer transition
                          ${isSelected
                            ? "border-[#3A9D23] bg-green-50"
                            : "border-gray-100 hover:border-gray-300"
                          }
                        `}
                      >
                        <div className="flex justify-between items-start">

                          <div>
                            <p className="font-medium text-sm text-gray-900">
                              {rate.ratePlanName}
                            </p>

                            {rate.mealPlan && rate.mealPlan !== "none" && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {rate.mealPlan === "breakfast" && "🍳 Petit-déjeuner inclus"}
                                {rate.mealPlan === "half_board" && "🍽️ Demi-pension"}
                                {rate.mealPlan === "full_board" && "🍽️ Pension complète"}
                              </p>
                            )}

                            <p className={`text-xs mt-1 ${policyColor}`}>
                              {policyText}
                            </p>
                          </div>

                          <div className="text-right shrink-0 ml-4">
                            <p className="font-semibold text-gray-900">
                              {rate.total} €
                            </p>
                            <p className="text-xs text-gray-400">
                              {rate.pricePerNight} € / nuit
                            </p>
                          </div>

                        </div>

                      </div>
                    );
                  })}

                </div>
              )}

            </div>
          );
        })}
      </div>

    </div>
  );
}

export default RoomList;